import React, { useState } from 'react';
import { Box, Button, FormControl, FormLabel, Input, VStack, Heading, Text, useToast, Alert, AlertIcon } from '@chakra-ui/react';
import RegistrationForm from './UserRegistrationForm';

interface LoginFormProps {
  onClose: () => void;
}

interface LoginResponseData {
  accessToken: string;
  userId: number;
  userType: number;
}

interface LoginResponse {
  data: LoginResponseData;
  message: string;
  statusCode: number;
}
const Base_apiUrl=process.env.REACT_APP_API_Base_Url;
const LoginForm: React.FC<LoginFormProps> = ({ onClose }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRegistration, setShowRegistration] = useState(false);
  const toast = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      const apiUrl = `${Base_apiUrl}Auth/login`;
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ username, password }),
      });
      
      const data: LoginResponse = await response.json();

      if (data.statusCode === 1 && data.data) {
        // Store token in localStorage for future API calls
        localStorage.setItem('accessToken', data.data.accessToken);
        localStorage.setItem('userId', data.data.userId.toString());
        localStorage.setItem('userType', data.data.userType.toString());
        
        toast({
          title: 'Login Successful',
          description: data.message || 'You have been authenticated successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        setTimeout(() => {
          onClose();
        }, 1500);
      } else if (data.statusCode === 0) {
        throw new Error(data.message || 'Invalid user credentials');
      } else {
        throw new Error('Invalid response from server');
      }
      
    } catch (error) {
      console.error('Login error:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Connection error';
      
      setError(errorMessage);
      
      toast({
        title: 'Authentication Failed',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (showRegistration) {
    return <RegistrationForm onClose={() => setShowRegistration(false)} />;
  }

  return (
    <Box
      position="fixed"
      top="50%"
      left="50%"
      transform="translate(-50%, -50%)"
      bg="white"
      p={6}
      borderRadius="md"
      boxShadow="lg"
      zIndex={1000}
      width="400px"
    >
      <Heading size="lg" mb={4}>MFA Protected Access</Heading>
      <Text mb={4}>Enter your credentials to access the system</Text>
      
      {error && (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      )}
      
      <form onSubmit={handleSubmit}>
        <VStack spacing={4}>
          <FormControl isRequired>
            <FormLabel>Username/email</FormLabel>
            <Input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username/email"
              autoComplete="username"
            />
          </FormControl>
          
          <FormControl isRequired>
            <FormLabel>Password</FormLabel>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              autoComplete="current-password"
            />
          </FormControl>
          
          <Button
            type="submit"
            colorScheme="blue"
            width="full"
            isLoading={isLoading}
            loadingText="Authenticating..."
          >
            Login
          </Button>
          
          <Button
            variant="outline"
            width="full"
            onClick={onClose}
          >
            Cancel
          </Button>

          <Text fontSize="sm" color="gray.600" textAlign="center">
            New user? {" "}
            <Button
              variant="link"
              colorScheme="blue"
              onClick={() => setShowRegistration(true)}
            >
              Register here
            </Button>
          </Text>
        </VStack>
      </form>
    </Box>
  );
};

export default LoginForm;